import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import ChatMessage, { Message } from "./ChatMessage";
import ChatInput from "./ChatInput";
import "./chat.css";

const WELCOME_MESSAGE =
  "Hey! I'm TorqAI's product assistant. Ask me anything about our AI agents: pricing, features, use cases. How can I help?";

const ChatWidget: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const [sessionId] = useState(() => crypto.randomUUID());
  const [remaining, setRemaining] = useState(20);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
      document.documentElement.style.overflow = "unset";
    }

    const panel = panelRef.current;
    if (panel) {
      const stopScroll = (e: WheelEvent | TouchEvent) => e.stopPropagation();
      panel.addEventListener("wheel", stopScroll, { passive: false });
      panel.addEventListener("touchmove", stopScroll, { passive: false });
      return () => {
        document.body.style.overflow = "unset";
        document.documentElement.style.overflow = "unset";
        panel.removeEventListener("wheel", stopScroll);
        panel.removeEventListener("touchmove", stopScroll);
      };
    }

    return () => {
      document.body.style.overflow = "unset";
      document.documentElement.style.overflow = "unset";
    };
  }, [isOpen]);

  function handleOpen() {
    setIsOpen(true);
    if (!hasOpened) setHasOpened(true);
  }

  async function handleSend(content: string) {
    const userMessage: Message = { role: "user", content };
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setIsLoading(true);

    try {
      const response = await fetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          messages: updatedMessages.filter((m) => m.role !== "system"),
          sessionId,
          page: location.pathname,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setMessages((prev) => [...prev, { role: "system", content: data.error }]);
        return;
      }

      setMessages((prev) => [...prev, { role: "assistant", content: data.reply }]);
      setRemaining(data.remaining);

      if (data.remaining <= 0) {
        setMessages((prev) => [
          ...prev,
          {
            role: "system",
            content:
              "I've been chatting for a while! For more help, leave your email and our team will follow up, or visit our contact page.",
          },
        ]);
      }
    } catch {
      setMessages((prev) => [
        ...prev,
        { role: "system", content: "Couldn't connect. Check your internet and try again." },
      ]);
    } finally {
      setIsLoading(false);
    }
  }

  function scrollBy(amount: number) {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ top: amount, behavior: "smooth" });
    }
  }

  const isLimitReached = remaining <= 0;

  return (
    <>
      {!isOpen && (
        <button className="chat-fab" onClick={handleOpen} aria-label="Open chat">
          <img src="https://main.dwr4mwxi7utxp.amplifyapp.com/favicon.png" alt="TorqAI Chat" style={{ width: "100%", height: "100%", borderRadius: "50%", padding: "10px" }} />
        </button>
      )}

      {isOpen && (
        <div 
          className="chat-panel"
          ref={panelRef}
        >
          <div className="chat-header">
            <div className="chat-header-left" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div className="chat-header-dp-wrapper">
                <img src="https://main.dwr4mwxi7utxp.amplifyapp.com/favicon.png" alt="TorqAI" className="chat-header-dp-img" />
              </div>
              <span className="chat-header-title">TorqAI Assistant</span>
            </div>
            <button
              className="chat-close-btn"
              onClick={() => setIsOpen(false)}
              aria-label="Close chat"
            >
              &times;
            </button>
          </div>

          <div className="chat-messages" ref={scrollRef} data-lenis-prevent="true">
            {hasOpened && (
              <ChatMessage message={{ role: "assistant", content: WELCOME_MESSAGE }} />
            )}
            {messages.map((msg, i) => (
              <ChatMessage key={i} message={msg} />
            ))}
            {isLoading && (
              <div className="chat-typing">
                <span className="chat-typing-dot" />
                <span className="chat-typing-dot" />
                <span className="chat-typing-dot" />
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <ChatInput onSend={handleSend} disabled={isLoading || isLimitReached} />
        </div>
      )}
    </>
  );
};

export default ChatWidget;
